import React, { useEffect, useMemo, useRef, useState } from 'react';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import { intlShape, injectIntl, FormattedMessage } from '../../util/reactIntl';
import { array, bool, element, func, node, object, oneOfType, shape, string } from 'prop-types';
import classNames from 'classnames';
import omit from 'lodash/omit';
import { propTypes, LISTING_STATE_CLOSED, LINE_ITEM_NIGHT, LINE_ITEM_DAY } from '../../util/types';
import { formatMoney } from '../../util/currency';
import { parse, stringify } from '../../util/urlHelpers';
import config from '../../config';
import { ReactComponent as BlankProfileImg } from '../../assets/svg/Blank_Profile.svg';
import {
  ModalInMobile,
  Button,
  Modal,
  ReviewModal,
  Form,
  FieldTextInput,
  FieldReviewRating,
  Avatar,
  IconSpinner,
} from '../../components';
import { BookingTimeForm } from '../../forms';
import arrayMutators from 'final-form-arrays';
import { Form as FinalForm } from 'react-final-form';
import css from './BookingPanel.module.css';
import { BiHeart } from 'react-icons/bi';
import moment from 'moment';
import routeConfiguration from '../../routeConfiguration';
import { createResourceLocatorString } from '../../util/routes';
import { ReactComponent as FavouriteIcon } from '../../assets/svg/favourites.svg';
import { TRANSITION_ASSIGNED_BY_BUSINESS } from '../../containers/ListingPage/ListingPage';
import {
  TRANSITION_ACCEPT_ASSIGNED_GIG,
  TRANSITION_AUTO_CONFIRM_ASSIGNED_GIG,
  TRANSITION_AUTO_ZERO_DECLINE_PAYMENT,
  TRANSITION_BUSINESS_NO_RESPONSE,
  TRANSITION_CANCEL,
  TRANSITION_COMPLETE,
  TRANSITION_COMPLETE_RESUMED_JOB,
  TRANSITION_DECLINE_ASSIGNED_GIG,
  TRANSITION_GIG_START_NOTIF_BEFORE_1,
  TRANSITION_GIG_START_NOTIF_BEFORE_24,
  TRANSITION_INITIATE_WORKER_PAYOUT,
  TRANSITION_PROVIDER_CANCEL,
  TRANSITION_PROVIDER_CANCEL_BEFORE_START_1,
  TRANSITION_PROVIDER_CANCEL_BEFORE_START_24,
  TRANSITION_STARTING_JOB,
  TRANSITION_WORKER_CANCEL,
  TRANSITION_WORKER_CANCEL_BEFORE_ACCEPT,
  TRANSITION_WORKER_CANCEL_BEFORE_START_1,
  TRANSITION_WORKER_CANCEL_BEFORE_START_24,
  TRANSITION_ZERO_CONFIRM_PAYMENT,
  TRANSITION_ZERO_DECLINE_PAYMENT,
  TRANSITION_ZERO_REQUEST_PAYMENT,
  txHasBeenAccepted,
  txHasBeenJobStarted,
} from '../../util/transaction';
import axios from 'axios';
import { apiBaseUrl } from '../../util/api';
import { isNull, set } from 'lodash';
import StarRating from '../StarRating/StarRating';
import { useDispatch, useSelector } from 'react-redux';
import { getUserReviews } from '../../ducks/user.duck';
import { blockedType, MIN_WORKER_START_HOUR } from '../../marketplace-custom-config';
import { useIsWorkerTimeslotBlocked } from '../../util/workerTimeslotBlockChecker';
import RatingAndComment from '../RatingAndComment/RatingAndComment';
// This defines when ModalInMobile shows content as Modal
const MODAL_BREAKPOINT = 1023;
const TODAY = new Date();

const rateBtnShowConditionSet = new Set([
  TRANSITION_COMPLETE,
  TRANSITION_COMPLETE_RESUMED_JOB,
  TRANSITION_INITIATE_WORKER_PAYOUT,
  TRANSITION_BUSINESS_NO_RESPONSE,
  'transition/reinitiate-payout-on-failure',
  'transition/update-worker-payout-status',
  'transition/check-payout-status',
  'transition/initiate-business-refund',
  'transition/check-payout-status-for-completion',
  'transition/re-initiate-business-refund-on-failure',
  'transition/confirm-and-complete-transaction',
]);

const appliedGigSet = new Set([
  TRANSITION_PROVIDER_CANCEL,
  TRANSITION_PROVIDER_CANCEL_BEFORE_START_1,
  TRANSITION_PROVIDER_CANCEL_BEFORE_START_24,
  TRANSITION_WORKER_CANCEL,
  TRANSITION_WORKER_CANCEL_BEFORE_ACCEPT,
  TRANSITION_WORKER_CANCEL_BEFORE_START_1,
  TRANSITION_WORKER_CANCEL_BEFORE_START_24,
  TRANSITION_GIG_START_NOTIF_BEFORE_1,
  TRANSITION_GIG_START_NOTIF_BEFORE_24,
  TRANSITION_ZERO_CONFIRM_PAYMENT,
  TRANSITION_ACCEPT_ASSIGNED_GIG,
  TRANSITION_AUTO_CONFIRM_ASSIGNED_GIG,
  TRANSITION_STARTING_JOB,
  TRANSITION_COMPLETE,
]);
const workerCancelGigSet = new Set([
  TRANSITION_WORKER_CANCEL_BEFORE_ACCEPT,
  TRANSITION_WORKER_CANCEL,
  TRANSITION_WORKER_CANCEL_BEFORE_START_1,
  TRANSITION_WORKER_CANCEL_BEFORE_START_24,
]);

const providerCancelGigSet = new Set([
  TRANSITION_PROVIDER_CANCEL,
  TRANSITION_PROVIDER_CANCEL_BEFORE_START_1,
  TRANSITION_PROVIDER_CANCEL_BEFORE_START_24,
  TRANSITION_ZERO_DECLINE_PAYMENT,
  TRANSITION_AUTO_ZERO_DECLINE_PAYMENT,
]);
// Get attribute: stripeAccountData
const getStripeAccountData = stripeAccount => stripeAccount.attributes.stripeAccountData || null;

// Check if there's requirements on selected type: 'past_due', 'currently_due' etc.
const hasRequirements = (stripeAccountData, requirementType) =>
  stripeAccountData != null &&
  stripeAccountData.requirements &&
  Array.isArray(stripeAccountData.requirements[requirementType]) &&
  stripeAccountData.requirements[requirementType].length > 0;

const priceData = (price, intl) => {
  if (price && price.currency === config.currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: `(${price.currency})`,
      priceTitle: `Unsupported currency (${price.currency})`,
    };
  }
  return {};
};

const openBookModal = (isOwnListing, isClosed, history, location) => {
  if (isOwnListing || isClosed) {
    window.scrollTo(0, 0);
  } else {
    const { pathname, search, state } = location;
    const searchString = `?${stringify({ ...parse(search), book: true })}`;
    history.push(`${pathname}${searchString}`, state);
  }
};

const closeBookModal = (history, location) => {
  const { pathname, search, state } = location;
  const searchParams = omit(parse(search), 'book');
  const searchString = `?${stringify(searchParams)}`;
  history.push(`${pathname}${searchString}`, state);
};

const dateFormattingOptions = { month: 'short', day: 'numeric', weekday: 'short' };

const BookingPanel = props => {
  const {
    rootClassName,
    className,
    titleClassName,
    listing,
    isOwnListing,
    unitType,
    onSubmit,
    title,
    subTitle,
    onManageDisableScrolling,
    onFetchTimeSlots,
    monthlyTimeSlots,
    history,
    location,
    intl,
    onFetchTransactionLineItems,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    listingAuthor,
    isProvider,
    currentUser,
    handleBookingInProgress,
    bookingInProgress,
    onUpdateProfile,
    onCustomUpdateProfile,
    alreadyApplied: propsAlreadyApplied,
    handleDeclineAssigned,
    assignedStatus,
    handleCancel,
    listingUserTx,
    userTxLastTransition,
    fetchStripeAccountError,
    stripeAccountFetched,
    btnLoading,
    workerBlockedTimeslots,
  } = props;

  const formRef = useRef(null);
  const dispatch = useDispatch();
  const reviewsDB = useSelector(state => state.user.userReviews);
  const onGetUserReviews = data => dispatch(getUserReviews(data));
  const [alreadyRatedByCurrentUser, setAlreadyRatedByCurrentUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [submitReviewLoading, setSubmitReviewLoading] = useState(false);
  const [declineLoading, setDeclineLoading] = useState(false);
  const [declineSuccess, setDeclineSuccess] = useState(false);
  const [isCategoryVerified, setIsCategoryVerified] = useState(false);
  const [isBlockedUser, setIsBlockedUser] = useState(false);
  const [openRateModal, setOpenRateModal] = useState(false);
  const [openDeclineModal, setOpenDeclineModal] = useState(false);
  const [stripeModal, setStripeModal] = useState(false);

  const reviewUrl = useMemo(() => {
    if (listing?.id?.uuid !== undefined && currentUser?.id?.uuid !== undefined) {
      return `${apiBaseUrl()}//api/reviews/fetchIsReviewedByUser`;
    }
    return null;
  }, [listing?.id?.uuid, currentUser?.id?.uuid]);

  const url = useMemo(() => {
    if (listingAuthor?.id?.uuid !== undefined && currentUser?.id?.uuid !== undefined) {
      return `${apiBaseUrl()}/api/user/isWorkerBlockedbyAuthor/${listingAuthor?.id?.uuid}/${currentUser?.id?.uuid
        }`;
    }
    return null;
  }, [listingAuthor?.id?.uuid, currentUser?.id?.uuid]);

  const isTimeslotBlocked = isProvider
    ? false
    : useIsWorkerTimeslotBlocked(listing, workerBlockedTimeslots);

  const { interviewDetails } = currentUser?.attributes?.profile?.privateData || {};
  const appliedLitingIds =
    currentUser && currentUser?.attributes?.profile?.publicData?.appliedListingId;
  const maxWorkerNeeded =
    listing?.attributes?.publicData?.appliedCust?.length?.toString() ===
    listing?.attributes?.publicData?.workerYouNeed;
  const requestAccepted =
    appliedLitingIds && appliedLitingIds?.length > 0 && !appliedLitingIds.includes(listing.id.uuid);
  const hasWorkerApplied = listing?.attributes?.publicData?.appliedCust?.includes(
    currentUser?.id?.uuid
  );
  const price = listing.attributes.price;
  const scheduleType = listing?.attributes?.publicData?.scheduleType;
  const companyName = listingAuthor?.attributes?.profile?.publicData?.companyName || '';
  const customId = listingAuthor?.attributes?.profile?.publicData?.customId || '';
  const isUserSuspended = !!currentUser?.attributes?.profile?.metadata?.isSuspended;
  const suspendType = currentUser?.attributes?.profile?.metadata?.suspendType;
  const timeZone =
    listing.attributes.availabilityPlan && listing.attributes.availabilityPlan.timezone;
  const hasListingState = !!listing.attributes.state;
  const isClosed = hasListingState && listing.attributes.state === LISTING_STATE_CLOSED;
  const showBookingTimeForm = hasListingState && !isClosed;
  const showClosedListingHelpText = listing.id && isClosed;
  const { formattedPrice, priceTitle } = priceData(price, intl);
  const isBook = !!parse(location.search).book;
  const reviwes = listing?.attributes?.reviews || [];
  const hasAccepted = !!(
    listing.attributes?.metadata?.acceptedWorkers !== undefined &&
    Array.isArray(listing.attributes.metadata.acceptedWorkers) &&
    listing.attributes.metadata.acceptedWorkers.includes(currentUser?.id?.uuid)
  );
  console.log(listing, hasAccepted);

  const subTitleText = !!subTitle
    ? subTitle
    : showClosedListingHelpText
      ? intl.formatMessage({ id: 'BookingPanel.subTitleClosedListing' })
      : null;

  const isNightly = unitType === LINE_ITEM_NIGHT;
  const isDaily = unitType === LINE_ITEM_DAY;

  const unitTranslationKey = isNightly
    ? 'BookingPanel.perNight'
    : isDaily
      ? 'BookingPanel.perDay'
      : 'BookingPanel.perUnit';

  const classes = classNames(rootClassName || css.root, className);
  const titleClasses = classNames(titleClassName || css.bookingTitle);

  const oneTimeStartTimeHour = parseInt(
    listing?.attributes?.publicData?.oneTimeScheduleList?.map(
      (m, indx) => m?.startTime?.hours?.value
    )
  );
  const oneTimeStartTimeMin = parseInt(
    listing.attributes.publicData.oneTimeScheduleList?.map((m, indx) => m?.startTime?.min?.value)
  );
  const oneTimeEndTimeHour = parseInt(
    listing.attributes.publicData.oneTimeScheduleList?.map((m, indx) => m?.endTime?.hours?.value)
  );
  const oneTimeEndTimeMin = parseInt(
    listing.attributes.publicData.oneTimeScheduleList?.map((m, indx) => m?.endTime?.min?.value)
  );
  const oneTimeUnpaidBreak = parseInt(
    listing.attributes.publicData.oneTimeScheduleList?.map((m, indx) => m?.unpaidBreak?.value)
  );

  const recStartTimeHour = parseInt(
    listing.attributes.publicData.recurringScheduleList?.map(
      (m, indx) => m?.startTime?.hours?.value
    )
  );
  const recStartTimeMin = parseInt(
    listing.attributes.publicData.recurringScheduleList?.map((m, indx) => m?.startTime?.min?.value)
  );
  const recEndTimeHour = parseInt(
    listing.attributes.publicData.recurringScheduleList?.map((m, indx) => m?.endTime?.hours?.value)
  );
  const recEndTimeMin = parseInt(
    listing.attributes.publicData.recurringScheduleList?.map((m, indx) => m?.endTime?.min?.value)
  );
  const recUnpaidBreak = parseInt(
    listing.attributes.publicData.recurringScheduleList?.map((m, indx) => m?.unpaidBreak?.value)
  );
  const startTimeInMin = oneTimeStartTimeHour * 60 + oneTimeStartTimeMin;
  const endTimeInMin = oneTimeEndTimeHour * 60 + oneTimeEndTimeMin;

  const recstartTimeInMin = recStartTimeHour * 60 + recStartTimeMin;
  const recendTimeInMin = recEndTimeHour * 60 + recEndTimeMin;

  let oneTimeWorkDurationInMIn = endTimeInMin - startTimeInMin - oneTimeUnpaidBreak;
  let recWorkDurationInMIn = recendTimeInMin - recstartTimeInMin - recUnpaidBreak;

  if (oneTimeWorkDurationInMIn < 0) {
    oneTimeWorkDurationInMIn += 24 * 60;
  }
  if (recWorkDurationInMIn < 0) {
    recWorkDurationInMIn += 24 * 60;
  }
  const durationHours = Math.floor(oneTimeWorkDurationInMIn / 60);
  const durationMinutes = oneTimeWorkDurationInMIn % 60;
  const recdurationHours = Math.floor(recWorkDurationInMIn / 60);
  const recdurationMinutes = recWorkDurationInMIn % 60;
  const isProfileVerified = currentUser?.attributes?.profile?.metadata?.isProfileVerified || false;
  const calculateStartDate = o => {
    const startTimeHour = parseInt(o?.startTime?.hours?.value, 10);
    const startTimeMinute = parseInt(o?.startTime?.min?.value, 10);

    const sDate = moment(o?.startDate?.date).set({
      hour: startTimeHour,
      minute: startTimeMinute,
    });

    return sDate;
  };
  const gigStartDate = !!listing?.attributes?.publicData?.gigStartDate
    ? listing?.attributes?.publicData?.gigStartDate
    : scheduleType === 'oneTime' &&
      listing?.attributes?.publicData?.oneTimeScheduleList &&
      listing?.attributes?.publicData?.oneTimeScheduleList?.[0].startDate?.date !== null
      ? moment(
        listing?.attributes?.publicData?.oneTimeScheduleList?.map(calculateStartDate)?.[0]
      ).format()
      : scheduleType === 'recurring' &&
        listing?.attributes?.publicData?.recurringScheduleList &&
        listing?.attributes?.publicData?.recurringScheduleList?.[0].startDate?.date !== null
        ? moment(
          listing?.attributes?.publicData?.recurringScheduleList?.map(calculateStartDate)?.[0]
        ).format()
        : null;

  const isPastGigApplyTime = moment()
    .add(MIN_WORKER_START_HOUR, 'hours')
    .isAfter(gigStartDate);

  const handleApply = () => {
    const { stripeAccount } = props;
    if (isBlockedUser || isPastGigApplyTime) {
      return null;
    }
    handleBookingInProgress(true);
    const stripeConnected =
      currentUser && currentUser.stripeAccount && !!currentUser.stripeAccount.id;

    const stripeAccountData = stripeConnected ? getStripeAccountData(stripeAccount) : null;

    const requirementsMissing =
      stripeAccount &&
      (hasRequirements(stripeAccountData, 'past_due') ||
        hasRequirements(stripeAccountData, 'currently_due'));
    if (!stripeAccountFetched && fetchStripeAccountError) {
    }
    if (requirementsMissing) {
      setStripeModal(true);
    } else if (isProfileVerified === false) {
      setShowModal(true);
      setIsCategoryVerified(true);
    } else {
      const hasCategory = !!currentUser?.attributes?.profile?.publicData?.jobCategories?.some(
        element =>
          element.category === listing?.attributes?.publicData?.jobCategories?.[0]?.category &&
          element.subCategory === listing?.attributes?.publicData?.jobCategories?.[0]?.subCategory
      );

      if (hasCategory) {
        setShowModal(false);
        setIsCategoryVerified(false);
        onSubmit();
      } else {
        setShowModal(true);
      }
    }
  };

  const handleAddSector = () => {
    const listingCategory = listing?.attributes?.publicData?.jobCategories?.[0];
    if (!listingCategory) return;

    const currentCategories = currentUser?.attributes?.profile?.publicData?.jobCategories || [];

    const hasCategory = currentCategories.some(
      cat =>
        cat.category === listingCategory.category &&
        cat.subCategory === listingCategory.subCategory
    );

    if (hasCategory) return;

    const updatedCategories = [...currentCategories, listingCategory];

    const updatePayload = {
      publicData: {
        jobCategories: updatedCategories,
        searchCategory: updatedCategories.map(c => c.category),
        searchSubCategory: updatedCategories.map(c => c.subCategory),
      },
    };

    const dbPayload = {
      dbData: {
        userId: currentUser?.id?.uuid,
        jobCategories: updatedCategories,
      },
      profile: updatePayload,
      dbFlags: {},
    };
    setShowModal(false);

    Promise.all([
      onUpdateProfile(updatePayload),
      onCustomUpdateProfile(dbPayload),
    ]).then(() => {
      handleBookingInProgress(false);
      onSubmit();
    });
  };

  const handleSave = e => {
    e.preventDefault();
    const Id = listing?.id?.uuid;

    let params;
    if (currentUser?.attributes?.profile?.protectedData?.favourites?.includes(Id)) {
      params = {
        favourites: currentUser?.attributes?.profile?.protectedData?.favourites?.filter(
          f => f !== Id
        ),
      };
    } else {
      params = {
        favourites: currentUser?.attributes?.profile?.protectedData?.favourites
          ? [...currentUser?.attributes?.profile?.protectedData?.favourites, Id]
          : [Id],
      };
    }
    const profile = {
      protectedData: { ...params },
    };
    const updatedValue = { ...profile };

    onUpdateProfile(updatedValue).then(() =>
      history.push(createResourceLocatorString('ListingPage', routeConfiguration(), {}, {}))
    );
  };

  const handleDecline = e => {
    e.preventDefault();
    setDeclineLoading(true);
    const listingId = listing?.id?.uuid;
    handleDeclineAssigned(listingId)
      .then(() => {
        setDeclineLoading(false);
        setDeclineSuccess(true);
        setOpenDeclineModal(false);
      })
      .catch(e => setDeclineLoading(false));
  };

  const setInterviewText = <FormattedMessage id="SetInterviewBar.SetInterviewBtnText" />;
  const isAssigned =
    !isProvider &&
    !isOwnListing &&
    currentUser?.attributes?.profile?.publicData?.assignedGigs?.includes(listing.id.uuid);
  const handleRateBusiness = () => {
    setOpenRateModal(true);
  };
  const handleRatingModalSubmit = async values => {
    if (alreadyRatedByCurrentUser) return;
    setSubmitReviewLoading(true);
    const apiBody = {
      isProvider,
      values,
      listingName: listing.attributes.title,
      workerId: currentUser.id.uuid,
      currentUserName: `${currentUser.attributes.profile.firstName} ${currentUser.attributes.profile.lastName}`,
      businessId: listingAuthor.id.uuid,
      otherUserName: listingAuthor?.attributes?.profile?.publicData?.companyName || '',
      listingId: listing.id.uuid,
    };
    const url = `${apiBaseUrl()}/api/reviews/submitReview/customer`;
    axios
      .post(url, apiBody)
      .then(res => {
        setOpenRateModal(false);
        formRef.current.reset();
        setAlreadyRatedByCurrentUser(true);
        setSubmitReviewLoading(false);
      })
      .catch(e => {
        setOpenRateModal(false);
        formRef.current.reset();
        setSubmitReviewLoading(false);
      });
  };
  const rateButtonCondition = rateBtnShowConditionSet.has(userTxLastTransition);
  const isAppledToGig = appliedGigSet.has(userTxLastTransition);
  const isWorkerCancelledGig = workerCancelGigSet.has(userTxLastTransition);
  const isProviderCancelledGig = providerCancelGigSet.has(userTxLastTransition);
  const totalRating =
    reviewsDB?.data?.length > 0
      ? reviewsDB.data.reduce((acc, curr, idx) => {
        const {
          communication_punctuality,
          workload_presentation,
          expectation_efficiency,
          condition_collaboration,
        } = curr;
        const sum =
          communication_punctuality +
          workload_presentation +
          expectation_efficiency +
          condition_collaboration;

        return acc + sum / 4;
      }, 0)
      : 0;
  const ratingCount = parseFloat(totalRating / parseInt(reviewsDB?.data?.length || 1)).toFixed(1);
  const alreadyApplied = isAppledToGig || propsAlreadyApplied;
  const isAccpted = listingUserTx && listingUserTx.id ? txHasBeenAccepted(listingUserTx) : false;
  const isJobStarted = txHasBeenJobStarted(listingUserTx);
  const btnContent = isProvider ? null : isTimeslotBlocked && !hasWorkerApplied ? (
    <p className={css.card}>
      <FormattedMessage id="BookingPanel.timeslotBlocked" />
    </p>
  ) : (
    <React.Fragment>
      {isBlockedUser && !isUserSuspended ? (
        <div className={css.card}>
          <FormattedMessage id="BookingPanel.CanNotApplyMsg" />
        </div>
      ) : null}
      {btnLoading ? (
        <IconSpinner />
      ) : isUserSuspended ? (
        <React.Fragment>
          <p className={css.card}>
            <FormattedMessage
              id={
                suspendType === blockedType.inDefinite
                  ? 'BookingPanel.indefiniteSuspendedUserMsg'
                  : 'BookingPanel.suspendedUserMsg'
              }
            />
          </p>
          {isWorkerCancelledGig ? null : isAccpted && !isJobStarted ? (
            <div className={css.btncontainer}>
              <Button
                id="cancelButton"
                type="button"
                className={classNames(css.declnbtn, {
                  [css.favoriteClicked]: false,
                })}
                onClick={() => props.setCancelState(true)}>
                <FormattedMessage id="WorkerExpForm.cancelBtn" />
              </Button>
            </div>
          ) : assignedStatus === TRANSITION_ACCEPT_ASSIGNED_GIG && !isJobStarted ? (
            <div className={css.btncontainer}>
              <Button
                id="cancelButton"
                onClick={() =>
                  props.setCancelState(true)
                }
                className={classNames(css.declnbtn, {
                  [css.favoriteClicked]: false,
                })}
              >
                <FormattedMessage id="WorkerExpForm.cancelBtn" />
              </Button>
            </div>
          ) : userTxLastTransition === TRANSITION_ZERO_REQUEST_PAYMENT ? (
            <div className={css.btncontainer}>
              <Button
                id="cancelButton"
                type="button"
                className={classNames(css.declnbtn, {
                  [css.favoriteClicked]: false,
                })}
                onClick={() => props.setCancelState(true)}
              >
                <FormattedMessage id="WorkerExpForm.cancelBtn" />
              </Button>
            </div>
          ) : null}
          {rateButtonCondition ? (
            <div className={css.btncontainer}>
              <Button
                id="rateButton"
                type="button"
                onClick={handleRateBusiness}
                disabled={alreadyRatedByCurrentUser}
                className={css.rate}
              >
                <FormattedMessage id="BookingPanel.rateTheBusiness" />
              </Button>
            </div>
          ) : null}
        </React.Fragment>
      ) : rateButtonCondition ? (
        <div className={css.btncontainer}>
          <Button
            id="rateButton"
            type="button"
            onClick={handleRateBusiness}
            disabled={alreadyRatedByCurrentUser}
            className={css.rate}
          >
            <FormattedMessage id="BookingPanel.rateTheBusiness" />
          </Button>
        </div>
      ) : isProviderCancelledGig ? (
        <p className={css.card}>
          <FormattedMessage id="BookingPanel.providerCancelJobRequest" />
        </p>
      ) : isWorkerCancelledGig ? (
        <p className={css.card}>
          <FormattedMessage id="BookingPanel.workerCancelJobRequest" />
        </p>
      ) :
        isAccpted ? (
          <div className={css.btncontainer}>
            <Button
              id="applied"
              className={classNames(css.applybtn, {
                [css.alreadyAppliedTag]: !!alreadyApplied || declineSuccess,
              })}
              disabled={true}
            >
              <FormattedMessage id="FilterForm.alreadyAcceptedByWorker" />
            </Button>
            {isJobStarted ? null : (
              <Button
                id="cancelButton"
                type="button"
                className={classNames(css.declnbtn, {
                  [css.favoriteClicked]: false,
                })}
                onClick={() => props.setCancelState(true)}>
                <FormattedMessage id="WorkerExpForm.cancelBtn" />
              </Button>
            )}
          </div>
        ) : assignedStatus === TRANSITION_ASSIGNED_BY_BUSINESS ? (
          <div className={css.btncontainer}>
            <Button
              id="acceptButton"
              className={classNames(css.applybtn, {
                [css.alreadyAppliedTag]: !!alreadyApplied || declineSuccess,
              })}
              inProgress={bookingInProgress}
              onClick={() => {
                !alreadyApplied && declineSuccess !== true && handleApply();
              }}
              disabled={!!alreadyApplied || declineSuccess || bookingInProgress || isPastGigApplyTime}
            >
              {alreadyApplied ? (
                <FormattedMessage id="InboxPage.stateAccepted" />
              ) : (
                <FormattedMessage id="TransactionPanel.acceptButton" />
              )}
            </Button>
            <Button
              id="declineButton"
              className={classNames(css.declnbtn, {
                [css.favoriteClicked]: false,
              })}
              onClick={() => setOpenDeclineModal(true)}
              disabled={declineLoading || declineSuccess}
              inProgress={declineLoading}
            >
              {declineSuccess ? (
                <FormattedMessage id="InboxPage.stateDeclined" />
              ) : (
                <FormattedMessage id="TransactionPanel.declineButton" />
              )}
            </Button>
          </div>
        ) : assignedStatus === TRANSITION_ACCEPT_ASSIGNED_GIG ? (
          <div className={css.btncontainer}>
            <Button
              id="acceptedButton"
              className={classNames(css.applybtn, {
                [css.alreadyAppliedTag]: !!alreadyApplied || declineSuccess,
              })}
              disabled={true}
            >
              <FormattedMessage id="InboxPage.stateAccepted" />
            </Button>
            {isJobStarted ? null : (
              <Button
                id="cancelButton"
                onClick={() =>
                  props.setCancelState(true)
                }
                className={classNames(css.declnbtn, {
                  [css.favoriteClicked]: false,
                })}
              >
                <FormattedMessage id="WorkerExpForm.cancelBtn" />
              </Button>
            )}
            {/* )} */}
          </div>
        ) : assignedStatus === TRANSITION_DECLINE_ASSIGNED_GIG ? (
          <p className={css.card}>
            <FormattedMessage id="BookingPanel.workerCancelJobRequest" />
          </p>
        ) : userTxLastTransition === TRANSITION_ZERO_REQUEST_PAYMENT ? (
          <div className={css.btncontainer}>
            <Button
              id="applied"
              className={classNames(css.applybtn, {
                [css.alreadyAppliedTag]: !!alreadyApplied || declineSuccess,
              })}
              disabled={true}
            >
              <FormattedMessage id="FilterForm.alreadyApplied" />
            </Button>
            <Button
              id="cancelButton"
              type="button"
              className={classNames(css.declnbtn, {
                [css.favoriteClicked]: false,
              })}
              onClick={() => props.setCancelState(true)}
            >
              <FormattedMessage id="WorkerExpForm.cancelBtn" />
            </Button>
            <button
              type="button"
              className={classNames(css.Savebtn, {
                [css.favoriteClicked]: currentUser?.attributes?.profile?.protectedData?.favourites?.includes(
                  listing?.id?.uuid
                ),
              })}
              onClick={handleSave}
            >
              <FavouriteIcon className={classNames(css.favourites)} />
              {currentUser?.attributes?.profile?.protectedData?.favourites?.includes(
                listing?.id?.uuid
              ) ? (
                <FormattedMessage id="BookingPanel.saved" />
              ) : (
                <FormattedMessage id="BookingPanel.saveJobs" />
              )}
            </button>
          </div>
        ) : (
          <div className={css.btncontainer}>
            {isTimeslotBlocked ? (
              <button
                type="button"
                className={classNames(css.Savebtn, {
                  [css.favoriteClicked]: currentUser?.attributes?.profile?.protectedData?.favourites?.includes(
                    listing?.id?.uuid
                  ),
                })}
                onClick={handleSave}
              >
                <FavouriteIcon className={classNames(css.favourites)} />
                {currentUser?.attributes?.profile?.protectedData?.favourites?.includes(
                  listing?.id?.uuid
                ) ? (
                  <FormattedMessage id="BookingPanel.saved" />
                ) : (
                  <FormattedMessage id="BookingPanel.saveJobs" />
                )}
              </button>
            ) : (
              <React.Fragment>
                <Button
                  className={classNames(css.applybtn, {
                    [css.alreadyAppliedTag]: !!alreadyApplied,
                  })}
                  inProgress={bookingInProgress}
                  onClick={() => {
                    // !alreadyApplied &&
                    //   !moment()
                    //     .add(6, 'hours')
                    //     .isAfter(gigStartDate) &&
                    handleApply();
                  }}
                  disabled={
                    !!alreadyApplied || isPastGigApplyTime || isBlockedUser
                    // ||  maxWorkerNeeded
                  }
                >
                  {!alreadyApplied ? (
                    <FormattedMessage id="FilterForm.submit" />
                  ) : (
                    <FormattedMessage id="FilterForm.alreadyApplied" />
                  )}
                </Button>
                {!alreadyApplied && isPastGigApplyTime ? (
                  <span className={css.pastGig}>
                    <FormattedMessage id="BookingPanel.pastGig" />
                  </span>
                ) : null}

                {alreadyApplied ? (
                  <Button
                    id="cancelButton"
                    type="button"
                    className={classNames(css.declnbtn, {
                      [css.favoriteClicked]: false,
                    })}
                  >
                    <FormattedMessage id="WorkerExpForm.cancelBtn" />
                  </Button>
                ) : (
                  <button
                    type="button"
                    className={classNames(css.Savebtn, {
                      [css.favoriteClicked]: currentUser?.attributes?.profile?.protectedData?.favourites?.includes(
                        listing?.id?.uuid
                      ),
                    })}
                    onClick={handleSave}
                  >
                    <FavouriteIcon className={classNames(css.favourites)} />
                    {currentUser?.attributes?.profile?.protectedData?.favourites?.includes(
                      listing?.id?.uuid
                    ) ? (
                      <FormattedMessage id="BookingPanel.saved" />
                    ) : (
                      <FormattedMessage id="BookingPanel.saveJobs" />
                    )}
                  </button>
                )}
              </React.Fragment>
            )}
          </div>
        )}
    </React.Fragment>
  );

  useEffect(() => {
    if (openRateModal && listing?.id?.uuid) {
      const data = { businessId: listing?.author?.id?.uuid, to: 'business' };
      onGetUserReviews(data);
    }
  }, [openRateModal]);
  useEffect(() => {
    if (reviewUrl)
      axios
        .post(`${apiBaseUrl()}/api/reviews/fetchIsReviewedByUser`, {
          listingId: listing?.id?.uuid,
          workerId: currentUser?.id?.uuid,
        })
        .then(res => {
          setAlreadyRatedByCurrentUser(res.data);
        })
        .catch(e => {
          setAlreadyRatedByCurrentUser(null);
        });
  }, [reviewUrl]);

  useEffect(() => {
    if (url) {
      const fetchData = async () => {
        try {
          const response = await axios.get(url, {
            withCredentials: true,
          });
          setIsBlockedUser(response?.data?.[0]?.block);
        } catch (error) { }
      };

      fetchData();
    }
  }, [url]);

  return (
    <div className={classes}>
      <ModalInMobile
        containerClassName={css.modalContainer}
        id="BookingTimeFormInModal"
        isModalOpenOnMobile={isBook}
        onClose={() => closeBookModal(history, location)}
        showAsModalMaxWidth={MODAL_BREAKPOINT}
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <div className={css.modalHeading}>
          <h1 className={css.title}>{title}</h1>
        </div>
        {/* <div className={css.btncontainer}>
                <button
                  className={css.applybtn}
                  onClick={() => {
                    history.push(
                      createResourceLocatorString(
                        'EditListingPage',
                        routeConfiguration(),
                        // {},
                        editParams
                      )
                    );
                  }}
                >
                  <FormattedMessage id="EditListingAvailabilityPanel.edit" />
                </button>
              </div> */}
        {btnContent}
        <div className={css.bookingHeading}>
          <div className={css.desktopPriceContainer}>
            <div className={css.desktopPerUnit}>
              <FormattedMessage id={unitTranslationKey} />
            </div>

            <div className={css.desktopPriceValue} title={priceTitle}>
              {formattedPrice}
              {listing?.attributes?.publicData?.workersEarnTip?.length > 0 && (
                <div className={css.tips}>
                  <span className={css.circle}></span>
                  <span className={css.canReceiveTips}>
                    <FormattedMessage id="advancemodal.tipsCheckBox" />
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className={css.OneTimehourDiv}>
            {scheduleType === 'oneTime' &&
              listing?.attributes?.publicData?.oneTimeScheduleList?.map((m, indx) => {
                return (
                  <React.Fragment key={indx}>
                    {indx > 0 ? (
                      <>
                        <hr />
                        <div className={css.workPerDay}>
                          <span className={css.desktopPerUnit}>
                            {durationHours ? <FormattedMessage id="SearchPage.workPerDay" /> : null}
                          </span>
                          &nbsp; <br />
                          <span className={css.value}>
                            {durationHours ? (
                              <>
                                {durationHours}
                                {` `}
                                <FormattedMessage id="ListingCardCustom.hours" />
                              </>
                            ) : null}
                            {durationMinutes !== 0 && typeof durationMinutes !== NaN
                              ? ` ${durationMinutes} minutes`
                              : null}
                          </span>{' '}
                          {parseInt(m?.unpaidBreak?.value) > 0 ? (
                            <>
                              <br />
                              <FormattedMessage id="SearchPage.unpaidBreak" />
                              &nbsp;
                              <br />
                              <span className={css.value}>{m?.unpaidBreak?.value} Minutes</span>
                              <br />
                            </>
                          ) : null}
                          <div className={css.scheduleDiv}>
                            <div className={css.workerCountDiv}>
                              {' '}
                              <span>
                                <FormattedMessage id="FieldDateTimeInput.startTime" /> :
                              </span>
                              <span>
                                {m?.startTime?.hours?.value} : {m?.startTime?.min?.value} &nbsp;-
                                &nbsp;
                              </span>{' '}
                            </div>
                            <div className={css.workerCountDiv}>
                              {' '}
                              <span>
                                <FormattedMessage id="FieldDateTimeInput.endTime" /> :
                              </span>
                              <span>
                                {m?.endTime?.hours?.value} : {m?.endTime?.min?.value}
                              </span>{' '}
                            </div>
                          </div>
                          {m?.startDate?.date ? (
                            <div className={css.daysdiv}>
                              <FormattedMessage id="SearchPage.Days" /> <br />
                              <span className={css.value}>
                                {' '}
                                {moment(m?.startDate?.date).format('Do MMM, y')}
                              </span>
                            </div>
                          ) : null}
                        </div>
                      </>
                    ) : (
                      <div className={css.workPerDay}>
                        <span className={css.desktopPerUnit}>
                          {durationHours ? <FormattedMessage id="SearchPage.workPerDay" /> : null}
                        </span>
                        &nbsp;
                        <br />
                        <span className={css.value}>
                          {durationHours ? (
                            <>
                              {durationHours}
                              {` `}
                              <FormattedMessage id="ListingCardCustom.hours" />
                            </>
                          ) : null}
                          {durationMinutes !== 0 && typeof durationMinutes !== NaN
                            ? ` ${durationMinutes} minutes`
                            : null}
                        </span>
                        {parseInt(m?.unpaidBreak?.value) > 0 ? (
                          <>
                            <br />
                            <FormattedMessage id="SearchPage.unpaidBreak" />
                            <br />
                            &nbsp;
                            <span className={css.value}>{m?.unpaidBreak?.value} Minutes</span>
                            <br />
                          </>
                        ) : null}
                        <div className={css.scheduleDiv}>
                          <div className={css.workerCountDiv}>
                            {' '}
                            <span>
                              <FormattedMessage id="FieldDateTimeInput.startTime" /> :
                            </span>
                            <span>
                              {m?.startTime?.hours?.value} : {m?.startTime?.min?.value} &nbsp;-
                              &nbsp;
                            </span>{' '}
                          </div>
                          <div className={css.workerCountDiv}>
                            {' '}
                            <span>
                              <FormattedMessage id="FieldDateTimeInput.endTime" /> :
                            </span>
                            <span>
                              {m?.endTime?.hours?.value} : {m?.endTime?.min?.value}
                            </span>{' '}
                          </div>
                        </div>
                        {m?.startDate?.date ? (
                          <div className={css.daysdiv}>
                            <FormattedMessage id="SearchPage.Days" /> <br />
                            <span className={css.value}>
                              {' '}
                              {moment(m?.startDate?.date).format('Do MMM, y')}
                            </span>
                          </div>
                        ) : null}
                      </div>
                    )}
                  </React.Fragment>
                );
              })}

            {scheduleType === 'recurring' &&
              listing?.attributes?.publicData?.recurringScheduleList?.map((r, indx) => {
                const recurringDays =
                  typeof r.recurringDays === 'string'
                    ? r.recurringDays.split(',')
                    : r.recurringDays.map(m => m);
                return (
                  <>
                    {r.startDate.date !== null ? (
                      <>
                        {indx > 0 ? (
                          <>
                            <hr />
                            <div className={css.recWorkperDay}>
                              <span className={css.desktopPerUnit}>
                                {recdurationHours ? (
                                  <FormattedMessage id="SearchPage.workPerDay" />
                                ) : null}
                              </span>
                              &nbsp;
                              <br />
                              <span className={css.value}>
                                {' '}
                                {recdurationHours ? (
                                  <>
                                    {recdurationHours}
                                    {` `}
                                    <FormattedMessage id="ListingCardCustom.hours" />
                                  </>
                                ) : null}
                                {recdurationMinutes !== 0 && typeof recdurationMinutes !== NaN
                                  ? ` ${recdurationMinutes} minutes`
                                  : null}
                              </span>{' '}
                              {parseInt(r?.unpaidBreak?.value) > 0 ? (
                                <>
                                  <br />
                                  <FormattedMessage id="SearchPage.unpaidBreak" />
                                  &nbsp;
                                  <br />
                                  <span className={css.value}>{r?.unpaidBreak?.value} Minutes</span>
                                </>
                              ) : null}
                            </div>
                            <div className={css.scheduleDiv}>
                              <div className={css.workerCountDiv}>
                                {' '}
                                <span>
                                  <FormattedMessage id="FieldDateTimeInput.startTime" /> :
                                </span>
                                <span>
                                  {r?.startTime?.hours?.value} : {r?.startTime?.min?.value} &nbsp;-
                                  &nbsp;
                                </span>{' '}
                              </div>
                              <div className={css.workerCountDiv}>
                                {' '}
                                <span>
                                  <FormattedMessage id="FieldDateTimeInput.endTime" /> :
                                </span>
                                <span>
                                  {r?.endTime?.hours?.value} : {r?.endTime?.min?.value}
                                </span>{' '}
                              </div>
                            </div>
                            <div className={css.workPerDay}>
                              <FormattedMessage id="SearchPage.recurringDays" />
                              &nbsp;
                              <br />
                              {recurringDays[0] === '' ? (
                                <>
                                  <span className={css.value}>
                                    <FormattedMessage id="SearchPage.Daily" />
                                  </span>
                                </>
                              ) : (
                                recurringDays.map((m, indx) =>
                                  indx > 0 ? (
                                    <>
                                      <span className={css.value} key={indx}>
                                        , {m}
                                      </span>
                                    </>
                                  ) : (
                                    <span className={css.value} key={indx}>
                                      {m}
                                    </span>
                                  )
                                )
                              )}
                              <br /> <FormattedMessage id="SearchPage.Days" /> <br />
                              <span className={css.value}>
                                {' '}
                                {moment(r?.startDate?.date).format('Do MMM, y')}
                              </span>{' '}
                              <FormattedMessage id="SearchPage.to" />{' '}
                              <span className={css.value}>
                                {' '}
                                {moment(r?.endDate?.date).format('Do MMM, y')}
                              </span>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className={css.recWorkperDay}>
                              <span className={css.desktopPerUnit}>
                                {recdurationHours ? (
                                  <FormattedMessage id="SearchPage.workPerDay" />
                                ) : null}
                              </span>
                              &nbsp;
                              <br />
                              <span className={css.value}>
                                {' '}
                                {recdurationHours ? (
                                  <>
                                    {recdurationHours}
                                    {` `}
                                    <FormattedMessage id="ListingCardCustom.hours" />
                                  </>
                                ) : null}
                                {recdurationMinutes !== 0 && typeof recdurationMinutes !== NaN
                                  ? ` ${recdurationMinutes} minutes`
                                  : null}
                              </span>{' '}
                              {parseInt(r?.unpaidBreak?.value) > 0 ? (
                                <>
                                  <br />
                                  <FormattedMessage id="SearchPage.unpaidBreak" />
                                  &nbsp;
                                  <br />
                                  <span className={css.value}>{r?.unpaidBreak?.value} Minutes</span>
                                </>
                              ) : null}
                            </div>
                            <div className={css.scheduleDiv}>
                              <div className={css.workerCountDiv}>
                                {' '}
                                <span>
                                  <FormattedMessage id="FieldDateTimeInput.startTime" /> :
                                </span>
                                <span>
                                  {r?.startTime?.hours?.value} : {r?.startTime?.min?.value} &nbsp;-
                                  &nbsp;
                                </span>{' '}
                              </div>
                              <div className={css.workerCountDiv}>
                                {' '}
                                <span>
                                  <FormattedMessage id="FieldDateTimeInput.endTime" /> :
                                </span>
                                <span>
                                  {r?.endTime?.hours?.value} : {r?.endTime?.min?.value}
                                </span>{' '}
                              </div>
                            </div>
                            <div className={css.workPerDay}>
                              <FormattedMessage id="SearchPage.recurringDays" />
                              &nbsp;
                              <br />
                              {recurringDays[0] === '' ? (
                                <>
                                  <span className={css.value}>
                                    <FormattedMessage id="SearchPage.Daily" />
                                  </span>
                                </>
                              ) : (
                                recurringDays.map((m, indx) =>
                                  indx > 0 ? (
                                    <>
                                      <span className={css.value}>, {m}</span>
                                    </>
                                  ) : (
                                    <span className={css.value}>{m}</span>
                                  )
                                )
                              )}
                              <br /> <FormattedMessage id="SearchPage.Days" /> <br />
                              <span className={css.value}>
                                {' '}
                                {moment(r?.startDate?.date).format('Do MMM, y')}
                              </span>{' '}
                              <FormattedMessage id="SearchPage.to" />{' '}
                              <span className={css.value}>
                                {' '}
                                {moment(r?.endDate?.date).format('Do MMM, y')}
                              </span>
                            </div>
                          </>
                        )}
                      </>
                    ) : null}
                  </>
                );
              })}
          </div>
          <div className={css.businessnamenlink}>
            {(isProvider || hasAccepted) && companyName ? (
              <React.Fragment>
                <h2
                  // className={css}
                  onClick={() =>
                    history.push(
                      createResourceLocatorString(
                        'BusinessProfileDisplayPage',
                        routeConfiguration(),
                        { id: listingAuthor?.id?.uuid },
                        {}
                      )
                    )
                  }
                >
                  {companyName}
                </h2>
                {customId ? (
                  <span>
                    {intl.formatMessage({ id: 'ProfileSettingsPage.businessID' })}{' '}
                    {customId.toUpperCase()}
                  </span>
                ) : null}
              </React.Fragment>
            ) : null}

            {isProvider ? null : (
              <div className={css.ratingWrapper}>
                <RatingAndComment
                  onSubmit={() => {}}
                  intl={intl}
                  currentUser={currentUser}
                  onManageDisableScrolling={onManageDisableScrolling}
                  history={history}
                  staffData={{ data: listingAuthor }}
                  reviewsDB={reviwes}
                  showDetailedReviews={false}
                  ratingPointContainerClassName={css.ratingPointContainer}
                />
              </div>
            )}
          </div>
        </div>
        {showBookingTimeForm ? (
          <BookingTimeForm
            className={css.bookingForm}
            formId="BookingPanel"
            submitButtonWrapperClassName={css.submitButtonWrapper}
            unitType={unitType}
            onSubmit={onSubmit}
            price={price}
            listingId={listing.id}
            isOwnListing={isOwnListing}
            monthlyTimeSlots={monthlyTimeSlots}
            onFetchTimeSlots={onFetchTimeSlots}
            startDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
            endDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
            timeZone={timeZone}
            onFetchTransactionLineItems={onFetchTransactionLineItems}
            lineItems={lineItems}
            fetchLineItemsInProgress={fetchLineItemsInProgress}
            fetchLineItemsError={fetchLineItemsError}
          />
        ) : null}
      </ModalInMobile>
      <div className={css.openBookingForm}>
        <div className={css.priceContainer}>
          <div className={css.priceValue} title={priceTitle}>
            {formattedPrice}
          </div>
          <div className={css.perUnit}>
            <FormattedMessage id={unitTranslationKey} />
          </div>
        </div>

        {/* {showBookingTimeForm ? (
            <Button
              rootClassName={css.bookButton}
              onClick={() => openBookModal(isOwnListing, isClosed, history, location)}
            >
              <FormattedMessage id="BookingPanel.ctaButtonMessage" />
            </Button>
          ) : isClosed ? (
            <div className={css.closedListingButton}>
              <FormattedMessage id="BookingPanel.closedListingButtonText" />
            </div>
          ) : null} */}
        {btnContent}
      </div>
      <Modal
        className={css.NoJobCategoryModal}
        id="BookingPanel.NoCategoryModal"
        isOpen={!!showModal}
        onClose={() => {
          setShowModal(false);
          handleBookingInProgress(false);
        }}
        onManageDisableScrolling={onManageDisableScrolling}
        usePortal
        containerClassName={classNames({
          [css.invoiceModal]: isProfileVerified === false,
        })}
      >
        <div className={css.appliedModalDiv}>
          <div className={css.modalTitle}>
            {isProfileVerified === false && !interviewDetails ? (
              // <FormattedMessage id="ListingPage.profileUnverified" />
              // <FormattedMessage
              //   className={css.nestedSpanText}
              //   id="ListingPage.profileUnverified"
              //   // values={{
              //   //   setInterview: { setInterviewText },
              //   // }}
              // />
              <FormattedMessage id="ListingPage.scheduleInterview" />
            ) : isProfileVerified === false ? (
              <FormattedMessage id="ListingPage.profileUnverified" />
            ) : isCategoryVerified === false ? (
              <FormattedMessage id="ListingPage.categoryNotMatched" />
            ) : (
              <FormattedMessage id="ListingPage.categoryUnverified" />
            )}
          </div>
          <div className={css.modalDesc}>
            {isProfileVerified === false && !interviewDetails ? (
              <p className={css.applyConditionModalText}>
                <FormattedMessage
                  id="ListingPage.profileUnverifiedDesc"
                  className={css.applyConditionModalText}
                />
              </p>
            ) : isProfileVerified === false ? (
              <>
                <p className={css.applyConditionModalText}>
                  <FormattedMessage id="ListingPage.ProfileUnverifiedDesc1" />
                </p>
                <p className={css.applyConditionModalText}>
                  <FormattedMessage id="ListingPage.ProfileUnverifiedDesc2" />
                </p>
                <p className={css.applyConditionModalText}>
                  <FormattedMessage id="ListingPage.BestOfLuck" />
                </p>
              </>
            ) : isCategoryVerified === false ? (
              <>
                <p className={css.applyConditionModalText}>
                  <FormattedMessage
                    id="ListingPage.categoryNotMatchedDesc"
                    className={css.applyConditionModalText}
                  />
                </p>
                <p className={css.applyConditionModalText}>
                  <FormattedMessage id="ListingPage.categoryNotMatchedDesc2" />
                </p>
              </>
            ) : (
              <>
                <p className={css.applyConditionModalText}>
                  <FormattedMessage id="ListingPage.categoryNotVerifiedDesc" />
                </p>
                <p className={css.applyConditionModalText}>
                  <FormattedMessage id="ListingPage.AskToVerifyDesc" />
                </p>
              </>
            )}
          </div>
          <div className={css.buttonRow}>
            <Button
              type="button"
              className={css.submitButton}
              onClick={handleAddSector}
            >
              {isProfileVerified === false ? (
                <FormattedMessage id="ContactDetailsForm.TurnOnAuth" />
              ) : (
                <FormattedMessage id="BookingPanel.addIndustryAndApply" />
              )}
            </Button>

            <div
              type="button"
              className={css.cancelBtn}
              onClick={() => {
                setShowModal(false);
                handleBookingInProgress(false);
              }}
            >
              <FormattedMessage id="ProfileSettingsForm.Cancel" />
            </div>
          </div>
        </div>
      </Modal>
      <Modal
        id="BookingPanel.RateModal"
        isOpen={!!openRateModal}
        onClose={() => {
          setOpenRateModal(false);
        }}
        onManageDisableScrolling={onManageDisableScrolling}
        usePortal
      >
        <FinalForm
          {...props}
          mutators={{ ...arrayMutators }}
          onSubmit={handleRatingModalSubmit}
          render={fieldRenderProps => {
            const { currentUser, handleSubmit, intl, values, form } = fieldRenderProps;
            if (!formRef.current) formRef.current = form;
            const messagePlaceholder = intl.formatMessage({
              id: 'ReviewModal.messagePlaceholder',
            });
            const ListingAuthorName = listingAuthor?.attributes?.profile?.publicData?.companyName;
            const ListingAuthorId = listingAuthor?.attributes?.profile?.publicData?.customId;
            const ListingAuthorImage =
              listingAuthor?.profileImage?.attributes?.variants?.default?.url;
            const currentRating = parseFloat(
              (parseInt(values?.communication || 0) +
                parseInt(values?.workload || 0) +
                parseInt(values?.expectation || 0) +
                parseInt(values?.condition || 0)) /
              4
            ).toFixed(1);
            return (
              <Form onSubmit={handleSubmit}>
                <div className={css.ReviewModalWrapper}>
                  <div className={css.HeadingModalContainer}>
                    <div className={css.reviewmodalHeading}>
                      <FormattedMessage id="ReviewModal.ProvideFeedback" />
                    </div>
                    <div className={css.modalsubHeading}>
                      <FormattedMessage id="ReviewModal.subHeading" />
                    </div>
                  </div>
                  <div className={css.businessDeatilsContainer}>
                    <div className={css.businessNameAndImg}>
                      {listingAuthor?.profileImage !== null ? (
                        <div className={css.businessImg}>
                          <img
                            src={ListingAuthorImage}
                            alt={ListingAuthorName}
                            className={css.profileImg}
                          />
                        </div>
                      ) : (
                        <BlankProfileImg className={css.profileImg} />
                      )}
                      <div className={css.busienssInfoDiv}>
                        <div className={css.businessName}>{ListingAuthorName}</div>
                        <div className={css.businessId}>
                          <span>
                            {' '}
                            <FormattedMessage id="ProfileSettingsPage.businessID" />
                          </span>{' '}
                          {ListingAuthorId}
                        </div>
                      </div>
                    </div>
                    <div className={css.RatingContainer}>
                      <div>
                        {' '}
                        <FormattedMessage id="ReviewModal.overallRating" />
                      </div>
                      <div className={css.ratingDiv}>
                        <span>{currentRating}</span>
                        <span>
                          <StarRating rating={currentRating} />
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className={css.RatingDetails}>
                    <div className={css.pointContainer}>
                      <div className={css.pointHeading}>
                        <FormattedMessage id="RatingAndComments.CommunicationAndFeedBack" />
                      </div>
                      <div className={css.pointsubHeading}>
                        <FormattedMessage id="ReviewModal.CommunicationAndFeedBackDesc" />
                      </div>
                      <div className={css.ratingDiv}>
                        <span>
                          <FieldReviewRating
                            id={`rate2`}
                            name="communication"
                            className={css.FieldReviewRating1}
                          />
                        </span>
                      </div>
                    </div>
                    <div className={css.pointContainer}>
                      <div className={css.pointHeading}>
                        <FormattedMessage id="RatingAndComments.workLoad" />
                      </div>
                      <div className={css.pointsubHeading}>
                        <FormattedMessage id="RatingAndComments.workLoadDesc" />
                      </div>
                      <div className={css.ratingDiv}>
                        <span>
                          <FieldReviewRating
                            id={`rate3`}
                            name="workload"
                            className={css.FieldReviewRating1}
                          />
                        </span>
                      </div>
                    </div>
                    <div className={css.pointContainer}>
                      <div className={css.pointHeading}>
                        <FormattedMessage id="RatingAndComments.clearexpectation" />
                      </div>
                      <div className={css.pointsubHeading}>
                        <FormattedMessage id="RatingAndComments.clearexpectationDesc" />
                      </div>
                      <div className={css.ratingDiv}>
                        <span>
                          <FieldReviewRating
                            id={`rate4`}
                            name="expectation"
                            className={css.FieldReviewRating1}
                          />
                        </span>
                      </div>
                    </div>
                    <div className={css.pointContainer}>
                      <div className={css.pointHeading}>
                        <FormattedMessage id="RatingAndComments.safeWorking" />
                      </div>
                      <div className={css.pointsubHeading}>
                        <FormattedMessage id="RatingAndComments.safeWorkingDesc" />
                      </div>
                      <div className={css.ratingDiv}>
                        <span>
                          <FieldReviewRating
                            id={`rate5`}
                            name="condition"
                            className={css.FieldReviewRating1}
                          />
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className={css.commentDiv}>
                    <div className={css.commentheading}>
                      <FormattedMessage id="ReviewModal.CommentHeading" />
                    </div>
                    <FieldTextInput
                      className={'textfield'}
                      type="textarea"
                      name="message"
                      autoComplete="message"
                      placeholder={messagePlaceholder}
                    />
                    <Button
                      inProgress={submitReviewLoading}
                      className={css.modalSubmitBtn}
                      disabled={Object.keys(values).length === 0}
                    >
                      <FormattedMessage id="ContactUsForm.signUp" />
                    </Button>
                  </div>
                </div>
              </Form>
            );
          }}
        />
      </Modal>
      <Modal
        id="BookingPanel.DeclineModal"
        isOpen={!!openDeclineModal}
        onClose={() => {
          setOpenDeclineModal(false);
        }}
        onManageDisableScrolling={onManageDisableScrolling}
        usePortal
        className={css.DeclineModal}
        showCloseButton={false}>
        <div className={css.declineModalContainer}>
          <div className={css.declineHeading}>
            <FormattedMessage id="BookingPanel.declineHeading" />
          </div>
          <div className={css.declineSubHeading}>
            <FormattedMessage id="BookingPanel.declineSubHeading" />
          </div>
          <div className={css.BtnDiv}>
            <Button className={css.YesBtn} onClick={handleDecline}>
              <FormattedMessage id="FieldBoolean.yes" />
            </Button>
            <button
              type="button"
              className={css.NoBtn}
              onClick={() => {
                setOpenDeclineModal(false);
              }}
            >
              <FormattedMessage id="FieldBoolean.no" />
            </button>
          </div>
        </div>
      </Modal>
      <Modal
        id="BookingPanel.stripe"
        isOpen={!!stripeModal}
        onClose={() => {
          setStripeModal(false);
          handleBookingInProgress(false);
        }}
        onManageDisableScrolling={onManageDisableScrolling}
        usePortal
        className={css.DeclineModal}
      >
        <div className={css.declineModalContainer}>
          <div className={css.declineHeading}>
            <FormattedMessage id="BookingPanel.addPayoutDetails" />
          </div>
          <div className={css.declineSubHeading}>
            <FormattedMessage id="BookingPanel.addPayoutDetailsDesc" />
          </div>
          <div className={css.BtnDiv}>
            <Button
              className={css.modalSubmitBtn}
              onClick={() =>
                history.push(
                  createResourceLocatorString('StripePayoutPage', routeConfiguration(), {}, {})
                )
              }
            >
              <FormattedMessage id="BookingPanel.goToAccount" />
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

BookingPanel.defaultProps = {
  rootClassName: null,
  className: null,
  titleClassName: null,
  isOwnListing: false,
  subTitle: null,
  unitType: config.bookingUnitType,
  monthlyTimeSlots: null,
  lineItems: null,
  fetchLineItemsError: null,
};

BookingPanel.propTypes = {
  rootClassName: string,
  className: string,
  titleClassName: string,
  listing: oneOfType([propTypes.listing, propTypes.ownListing]),
  isOwnListing: bool,
  unitType: propTypes.bookingUnitType,
  onSubmit: func.isRequired,
  title: oneOfType([node, string]).isRequired,
  subTitle: oneOfType([node, string]),
  authorDisplayName: oneOfType([node, string]).isRequired,
  onManageDisableScrolling: func.isRequired,
  onFetchTimeSlots: func.isRequired,
  monthlyTimeSlots: object,
  onFetchTransactionLineItems: func.isRequired,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,

  // from withRouter
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({
    search: string,
  }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

export default compose(
  withRouter,
  injectIntl
)(BookingPanel);
